import classNames from 'classnames';
import { StaticImage } from 'gatsby-plugin-image';
import { useEffect, useRef, useState } from 'react';

import { Phone } from '~components/AllianceCallService/AllianceCallService';
import Button from '~components/Button/Button';
import { usePageContext } from '~components/PageDataProvider/PageDataProvider';
import Trustpilot from '~components/Trustpilot/Trustpilot';
import ZipForm from '~components/ZipCodeForm/ZipCodeForm';
import { useAppDispatch, useAppSelector } from '~hooks/useRedux';
import useWindowSize from '~hooks/useWindowSize';
import CloseIcon from '~images/inline/close.inline.svg';
import { toggleStickyEmailGuide } from '~redux/Actions/DisplayActions';

// TODO: combine this component with StickyCtaBanner

const ScrollToSticky = () => {
    const [currentScroll, setCurrentScroll] = useState(0);
    const [initialHeight, setInitialHeight] = useState(0);
    const [isDismissed, setIsDismissed] = useState(false);
    const [isPinned, setIsPinned] = useState(false);
    const [planType, setPlanType] = useState('MA' as PlansType);

    const stateName = useAppSelector((state) => state.global.geoLocation.stateName);
    const isExpanded = useAppSelector((state) => state.display.stickyGuideOpen);
    const pageData = usePageContext();
    const throttle = useAppSelector((state) => state.global.throttle);

    const container = useRef(null as null | HTMLDivElement);

    const dispatch = useAppDispatch();

    const currentYear = pageData.annualCostData.customProperties.CURRENT_YEAR.value;

    const { isDesktop } = useWindowSize();

    const handleClose = () => {
        setIsPinned(false);
        setIsDismissed(true);
    };

    const waLink: string =
        'Click:' + process.env.GATSBY_ANALYTICS_SITE_GROUP + ':ReadytoFindaPlan:GetStarted';
    const waLinkSticky: string =
        'Sticky:Click:' + process.env.GATSBY_ANALYTICS_SITE_GROUP + ':ReadytoFindaPlan:GetStarted';

    useEffect(() => {
        if (isPinned && isExpanded) {
            dispatch(toggleStickyEmailGuide(false));
        }
    }, [isPinned]);

    useEffect(() => {
        const onScroll = () => {
            const isScrolledPast =
                window.scrollY >=
                (container.current?.offsetTop as number) +
                    (container.current?.offsetHeight as number);
            const diyZipForm = document.querySelector('[data-qa="zip-form-LiveFindPlanNeedHelp"]');
            const hasReachedDiyZipForm =
                diyZipForm &&
                window.scrollY + window.innerHeight >= (diyZipForm as HTMLElement).offsetTop;

            setCurrentScroll(window.scrollY);
            if (isScrolledPast && !hasReachedDiyZipForm && !isPinned && !isDismissed) {
                setIsPinned(true);
            }
            if ((!isScrolledPast || hasReachedDiyZipForm) && isPinned) {
                setIsPinned(false);
            }
        };
        window.addEventListener('scroll', onScroll, { passive: true });
        return () => window.removeEventListener('scroll', onScroll);
    }, [currentScroll]);

    useEffect(() => {
        setInitialHeight(container.current?.offsetHeight as number);
        const tags = pageData.tags?.map((tag) => tag.slug);
        if (tags) {
            if (tags.includes('type-ma')) {
                setPlanType('MA');
            } else if (tags.includes('type-ms')) {
                setPlanType('MS');
            } else if (tags.includes('type-md')) {
                setPlanType('MD');
            }
        }
    }, []);

    if (throttle !== 'GREEN' && planType === 'MS') return null;

    return (
        <div style={{ minHeight: initialHeight }} ref={container}>
            <div
                className={classNames(
                    'text-center bg-surface-0',
                    'px-3 pb-3 pt-2',
                    'md:pb-0 md:pt-0',
                    'bottom-0 left-0',
                    'flex items-end w-full',
                    isPinned ? 'fixed w-full z-10' : 'relative',
                    isPinned && 'pt-8',
                    isPinned && 'md:justify-center md:items-center md:gap-6 md:pb-3',
                    !isPinned && 'rounded-md',
                    !isPinned && 'md:pt-12 lg:pt-0'
                )}
            >
                <div
                    className={classNames(
                        'hidden flex-shrink-0 flex-grow-0',
                        isPinned ? 'md:hidden' : 'md:block'
                    )}
                >
                    <StaticImage
                        src="../../images/people/woman-smiling.png"
                        alt=""
                        className="mr-7 md:w-36 lg:w-40"
                    />
                </div>
                <div
                    className={classNames(
                        'flex-grow-0 w-full',
                        isPinned && 'flex items-center justify-center gap-6 md:pt-4'
                    )}
                >
                    <p
                        className={classNames(
                            'font-poppins font-bold text-link-visited',
                            'text-center',
                            'text-16 md:text-24 leading-24 md:leading-32',
                            !isPinned && 'pb-2 md:pb-3 md:mt-3',
                            isPinned && 'hidden lg:block',
                            !isPinned && 'md:absolute lg:relative left-0 top-0 w-full'
                        )}
                    >
                        Compare {currentYear} Medicare{' '}
                        {planType === 'MS' ? 'Supplement' : 'Advantage'} plans
                        {stateName && ` in ${stateName}`}.
                    </p>

                    <div
                        className={classNames(
                            isPinned
                                ? 'hidden'
                                : 'relative flex justify-center flex-grow-0 flex-shrink-0 lg:px-20'
                        )}
                    >
                        {planType === 'MS' ? (
                            <div className="pb-2">
                                <p className="font-sans text-12 leading-16 text-center pb-3 md:pb-1 md:text-16 md:leading-24">
                                    Speak with an eHealth licensed insurance agent
                                </p>
                                <div className="block md:hidden">
                                    <Phone isbtn={'primary'} />
                                </div>
                                <div className="hidden md:block">
                                    <Phone classname="font-poppins font-bold text-primary md:text-40 md:leading-56" />
                                    <span className="font-poppins font-light md:text-40 md:leading-56 ml-3">
                                        TTY 711
                                    </span>
                                </div>
                            </div>
                        ) : (
                            <ZipForm
                                buttonWaLink={
                                    isDesktop ? 'ZipFormFindPlanDesktop:SubmitButton' : waLink
                                }
                                qaValue="FindPlan"
                                buttonTitle="See plans in my area"
                                opts={{
                                    dialogPosition: '-top-16',
                                    useWideLayout: true,
                                }}
                            />
                        )}
                    </div>

                    <div
                        className={classNames(
                            'py-2 md:pt-0 md:pb-3',
                            'mt-2 md:mt-0',
                            isPinned && 'hidden'
                        )}
                    >
                        <Trustpilot size="newthin" />
                    </div>

                    <div className={classNames(!isPinned && 'hidden', 'w-full md:w-auto')}>
                        {planType === 'MS' ? (
                            <div>
                                <div className="block md:hidden">
                                    <Phone isbtn={'primary'} />
                                </div>
                                <div className="hidden md:block">
                                    <p className="font-sans text-12 leading-16 text-center">
                                        Speak with an eHealth licensed insurance agent
                                    </p>
                                    <Phone classname="font-poppins font-bold text-primary md:text-24 md:leading-32" />
                                    <span className="font-poppins font-light md:text-24 md:leading-32 ml-3">
                                        TTY 711
                                    </span>
                                </div>
                            </div>
                        ) : (
                            <Button
                                islink
                                forceATag
                                href={process.env.GATSBY_EHMP_URL}
                                data-wa-link={
                                    isDesktop
                                        ? 'Sticky:ZipFormFindPlanDesktop:SubmitButton'
                                        : waLinkSticky
                                }
                            >
                                See plans in my area
                            </Button>
                        )}
                    </div>
                </div>
                {isPinned && (
                    <button
                        className={classNames(
                            'flex items-center',
                            'absolute top-2 right-0 md:top-4 md:right-4',
                            'pr-2 md:pt-2 md:pr-3 lg:pt-3 lg:pr-4',
                            'text-link-visited font-bold'
                        )}
                        type="button"
                        data-wa-link="Close ZIP CTA"
                        onClick={handleClose}
                    >
                        <span className="text-sm mr-1">Close</span>
                        <CloseIcon data-qa="close-cta-sticky" />
                    </button>
                )}
            </div>
        </div>
    );
};

export default ScrollToSticky;
